// Helpers de presentación de FichaPersonalViewer.tsx.
// Extraídos del componente principal en prompt 380 (O8) sin modificar comportamiento.
// Responsabilidad: subcomponentes visuales (Campo, Seccion), renderers de secciones
// solo-lectura (relaciones jerárquicas y roles de gobernanza en vivo) y estilos
// compartidos (estiloBtn, estiloTitulo).

import type React from 'react';
import type { PuestoItem } from '../../../shared/services/api';
import { C } from '@/lib/design';

// ── Tokens visuales (duplicados del componente principal para autosuficiencia) ──
const AZUL1 = C.azul1;
const AZUL2 = C.azul2;
const BORDE = '#E5E7EB';
const PENDIENTE = '#9CA3AF';

export const estiloTitulo: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  color: AZUL2,
  textTransform: 'uppercase',
};

// ── Subcomponentes ──────────────────────────────────────────────────────────

export function Campo({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#1A1D23' }}>{value}</div>
    </div>
  );
}

export function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section style={{ padding: '14px 28px', borderBottom: `1px solid ${BORDE}` }}>
      <h4 style={estiloTitulo}>{titulo}</h4>
      {children}
    </section>
  );
}

// ── Renderers de secciones en vivo (FP) ─────────────────────────────────────

export function renderRelacionesJerarquicas(
  puesto: PuestoItem | null,
  puestoTitulo: string | undefined,
  sinRelacionesMsg: string,
) {
  const padres = puesto?.reportaA ?? [];
  const hijos = puesto?.supervisa ?? [];
  if (padres.length === 0 && hijos.length === 0) {
    return (
      <div style={{ fontSize: 13, color: PENDIENTE, fontStyle: 'italic' }}>{sinRelacionesMsg}</div>
    );
  }
  const itemStyle: React.CSSProperties = { fontSize: 13, color: '#1A1D23', lineHeight: 1.7 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {padres.map((r) => (
        <div key={r.puestoPadre.id} style={itemStyle}>
          <span style={{ color: '#64748B', marginRight: 6 }}>↑</span>
          {r.puestoPadre.titulo}
          {r.puestoPadre.codigo && (
            <span
              style={{
                marginLeft: 8,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: '#94A3B8',
              }}
            >
              {r.puestoPadre.codigo}
            </span>
          )}
        </div>
      ))}
      <div style={{ ...itemStyle, fontWeight: 700, color: AZUL1 }}>
        <span style={{ color: AZUL2, marginRight: 6 }}>→</span>
        {puestoTitulo ?? '—'}
      </div>
      {hijos.map((r) => (
        <div key={r.puestoHijo.id} style={itemStyle}>
          <span style={{ color: '#64748B', marginRight: 6 }}>↓</span>
          {r.puestoHijo.titulo}
          {r.puestoHijo.codigo && (
            <span
              style={{
                marginLeft: 8,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: '#94A3B8',
              }}
            >
              {r.puestoHijo.codigo}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function renderRolesGobernanza(puesto: PuestoItem | null, sinRolesMsg: string) {
  const roles = puesto?.rolesGobernanza ?? [];
  if (roles.length === 0) {
    return <div style={{ fontSize: 13, color: PENDIENTE, fontStyle: 'italic' }}>{sinRolesMsg}</div>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {roles.map((r) => (
        <span
          key={r.id}
          style={{
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            background: '#EEF2FF',
            color: AZUL1,
            border: `1px solid ${AZUL2}33`,
          }}
        >
          {r.nombrePersonalizado ?? r.tipoRol}
          {r.ambito && (
            <span style={{ marginLeft: 6, color: '#64748B', fontWeight: 400 }}>· {r.ambito}</span>
          )}
        </span>
      ))}
    </div>
  );
}

// ── Estilos compartidos ─────────────────────────────────────────────────────

export function estiloBtn(primary: boolean): React.CSSProperties {
  return {
    padding: '7px 16px',
    borderRadius: 8,
    border: primary ? 'none' : `1px solid ${BORDE}`,
    background: primary ? AZUL1 : '#fff',
    color: primary ? '#fff' : '#374151',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}
